import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { MessageCircle, Phone, X, Send, Loader2 } from "lucide-react";
import { API_URL, SITE } from "../data/content";
import { useLang } from "../i18n";

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const ChatPanel = ({ onClose }) => {
  const { t, lang } = useLang();
  const [name, setName] = useState("");
  const [named, setNamed] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState(() => [{ from: "bot", text: t("chat_greeting") }]);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const text = input.trim();
    setMessages((m) => [...m, { from: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/chat`, { name, message: text, lang });
      setMessages((m) => [...m, { from: "bot", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { from: "bot", text: t("chat_err") }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      data-testid="live-chat-dialog"
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 24, scale: 0.96 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="fixed bottom-40 sm:bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] max-w-sm rounded-3xl bg-white shadow-2xl shadow-forest/25 border border-bone overflow-hidden flex flex-col"
    >
      <div className="bg-forest text-cream px-5 py-4 flex items-center justify-between">
        <div>
          <p className="font-display font-bold text-sm">{t("chat_title")}</p>
          <p className="text-[11px] text-cream/70">{t("chat_sub")}</p>
        </div>
        <button onClick={onClose} data-testid="live-chat-close-btn" className="p-1.5 hover:bg-white/10 rounded-full transition-colors" aria-label="Close chat">
          <X className="w-4 h-4" />
        </button>
      </div>

      {!named ? (
        <div className="p-5 space-y-4">
          <p className="text-sm text-bark">{t("chat_name_q")}</p>
          <input
            data-testid="live-chat-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("chat_name_ph")}
            className="w-full rounded-2xl border border-bone bg-cream/60 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-tree/50"
          />
          <button
            data-testid="live-chat-start-btn"
            disabled={!name.trim()}
            onClick={() => setNamed(true)}
            className="w-full rounded-full bg-tangerine text-white py-3 text-sm font-bold hover:bg-clay transition-colors disabled:opacity-50"
          >
            {t("chat_start")}
          </button>
          <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="block text-center text-xs font-semibold text-tree hover:text-tangerine transition-colors">
            {t("chat_prefer")}
          </a>
        </div>
      ) : (
        <>
          <div data-testid="live-chat-messages" className="h-72 overflow-y-auto p-4 space-y-3 bg-cream/40">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.from === "user" ? "bg-tree text-white rounded-br-sm" : "bg-white text-charcoal border border-bone rounded-bl-sm"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {sending && <div className="text-xs text-bark/60 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> {t("chat_typing")}</div>}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="p-3 border-t border-bone flex gap-2 bg-white">
            <input
              data-testid="live-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chat_input_ph")}
              className="flex-1 rounded-full border border-bone bg-cream/60 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-tree/50"
            />
            <button type="submit" data-testid="live-chat-send-btn" className="w-10 h-10 rounded-full bg-forest text-cream flex items-center justify-center hover:bg-tree transition-colors" aria-label="Send">
              <Send className="w-4 h-4" />
            </button>
          </form>
          <a href={SITE.whatsapp} target="_blank" rel="noreferrer" data-testid="live-chat-whatsapp-fallback"
             className="bg-[#25D366]/10 text-[#128C4A] text-center text-xs font-bold py-2.5 hover:bg-[#25D366]/20 transition-colors">
            {t("chat_wa")}
          </a>
        </>
      )}
    </motion.div>
  );
};

export const FloatingActions = () => {
  const { t } = useLang();
  const [chatOpen, setChatOpen] = useState(false);

  return (
    <>
      <AnimatePresence>{chatOpen && <ChatPanel onClose={() => setChatOpen(false)} />}</AnimatePresence>

      <div className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-40 flex flex-col gap-3 items-end">
        <motion.button
          data-testid="live-chat-toggle-btn"
          onClick={() => setChatOpen(!chatOpen)}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1.2, type: "spring", stiffness: 260 }}
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-forest text-cream shadow-xl shadow-forest/30 hover:scale-110 active:scale-95 transition-transform duration-300 flex items-center justify-center"
          aria-label="Open live chat"
        >
          {chatOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />}
        </motion.button>
        <motion.a
          data-testid="floating-call-btn"
          href={SITE.phoneHref}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1.05, type: "spring", stiffness: 260 }}
          className="p-3.5 rounded-full bg-amber text-charcoal shadow-xl shadow-amber/30 hover:scale-110 active:scale-95 transition-transform duration-300"
          aria-label="Call the school"
        >
          <Phone className="w-6 h-6" />
        </motion.a>
        <motion.a
          data-testid="floating-whatsapp-btn"
          href={SITE.whatsapp}
          target="_blank"
          rel="noreferrer"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.9, type: "spring", stiffness: 260 }}
          className="relative p-3.5 rounded-full bg-[#25D366] text-white shadow-xl shadow-[#25D366]/40 hover:scale-110 active:scale-95 transition-transform duration-300"
          aria-label="Chat on WhatsApp"
        >
          <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-20" />
          <WhatsAppIcon className="w-6 h-6 relative" />
        </motion.a>
      </div>

      <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-cream/95 backdrop-blur-lg border-t border-bone px-4 py-3 flex gap-3">
        <a href={SITE.phoneHref} data-testid="mobile-sticky-call-btn"
           className="flex-1 rounded-full border-2 border-forest text-forest py-3 text-sm font-bold text-center">
          {t("mobile_call")}
        </a>
        <a href="/contact" data-testid="mobile-sticky-tour-btn"
           className="flex-[1.4] rounded-full bg-tangerine text-white py-3 text-sm font-bold text-center shadow-lg shadow-tangerine/30">
          {t("mobile_tour")}
        </a>
      </div>
    </>
  );
};
